import React from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, IntlShape } from 'react-intl';

import {
    ShortCodeEnums,
    ReactTableTypes,
    ReactTableUtils,
    SortableTableHeader,
    CoreUtils,
} from '@nhl/nhl-breakaway-ui';
import { CellContext, createColumnHelper } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { createGenericSortableStatsCol, leftPaddedColumn } from '~/core/commonTableColumns';
import { Alignments, CountryCode } from '~/core/enums';
import { HEADSHOT_NAME_COL } from '~/core/tableColumns/playerTableColumns/columns';
import { FormatPlayerPosition, getPlayerHeight, getPlayerWeight, parseValueFromLocalizedAttribute } from '~/core/utils';
import { addSortableHeaderTitle } from '~/views/components/reactTable/utils';

import { Player } from './types';

import { BirthplaceColumn } from './styles';

const columnHelper = createColumnHelper<Player>();
export const createRosterCol = createGenericSortableStatsCol(columnHelper);

const columnDefinitions = (
    isGoalie: boolean,
    screenWidth: number,
    countryCode: CountryCode,
    isWebview: boolean,
    intl: IntlShape
) => [
    HEADSHOT_NAME_COL(
        intl,
        screenWidth,
        isWebview,
        isGoalie,
        ShortCodeEnums.TeamTriCode[window.__SITE_SETTINGS__.franchise?.triCode?.toUpperCase()]
    ),
    {
        ...createRosterCol(intl, {
            acc: 'sweaterNumber',
            header: CommonMessages.playerInfoDescriptions.abbreviations.number,
            meta: addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.abbreviations.number, intl),
        }),
        cell: (cell: CellContext<Player, number>) =>
            cell.getValue() || <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />,
    },
    {
        ...createRosterCol(intl, {
            acc: 'positionCode',
            header: CommonMessages.playerInfoDescriptions.abbreviations.position,
            meta: addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.position, intl),
        }),
        cell: (cell: CellContext<Player, string>) => <FormatPlayerPosition position={cell?.getValue()} />,
    },
    {
        ...createRosterCol(intl, {
            acc: 'shootsCatches',
            header: isGoalie
                ? CommonMessages.playerInfoDescriptions.abbreviations.catches
                : CommonMessages.playerInfoDescriptions.abbreviations.shoots,
            size: 50,
            meta: addSortableHeaderTitle(
                isGoalie
                    ? CommonMessages.playerInfoDescriptions.long.catches
                    : CommonMessages.playerInfoDescriptions.long.shoots,
                intl
            ),
        }),
        cell: (cell: CellContext<Player, string>) =>
            cell.getValue() ? (
                <span
                    aria-label={CoreUtils.safeFormatMessage(
                        intl,
                        CommonMessages.playerInfoDescriptions.handedness.long[cell.getValue()]
                    )}
                >
                    <CoreUtils.SafeFormattedMessage
                        message={CommonMessages.playerInfoDescriptions.handedness.abbreviations[cell.getValue()]}
                    />
                </span>
            ) : null,
    },
    {
        ...createRosterCol(intl, {
            acc: 'heightInInches',
            header: CommonMessages.playerInfoDescriptions.abbreviations.height,
            size: 70,
            meta: addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.height, intl),
        }),
        cell: ({ cell, row }: CellContext<Player, number>) =>
            cell.getValue() ? (
                getPlayerHeight(countryCode, row.original.heightInInches)
            ) : (
                <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
            ),
        sortDescFirst: true,
    },
    {
        ...createRosterCol(intl, {
            acc: 'weightInPounds',
            header: CommonMessages.playerInfoDescriptions.abbreviations.weight,
            meta: addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.weight, intl),
        }),
        cell: ({ cell, row }: CellContext<Player, number>) =>
            cell.getValue() ? (
                getPlayerWeight(countryCode, row.original.weightInPounds, row.original.weightInKilograms, true)
            ) : (
                <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
            ),
        sortDescFirst: true,
    },
    {
        ...createRosterCol(intl, {
            acc: 'birthDate',
            header: CommonMessages.playerInfoDescriptions.long.born,
            size: 120,
            alignHeader: Alignments.left,
            meta: {
                ...addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.born, intl),
                ...leftPaddedColumn(),
            },
        }),
        cell: (cell: CellContext<Player, string>) =>
            DateTime.fromISO(cell.getValue()).toLocaleString(DateTime.DATE_MED),
    },

    columnHelper.accessor('birthCountry', {
        header: ({ sortedState }: ReactTableTypes.HeaderContextType<Player, string>) => (
            <SortableTableHeader
                sortIndex={sortedState?.index}
                sortDesc={sortedState?.desc}
                alignment={Alignments.left}
            >
                <FormattedMessage {...CommonMessages.playerInfoDescriptions.long.birthplace} />
            </SortableTableHeader>
        ),
        cell: ({ row }) => {
            const {
                birthCountry,
                birthCity: unparsedBirthCity,
                birthStateProvince: unparsedBirthStateProvince,
            } = row.original;
            const birthCity = unparsedBirthCity !== undefined && parseValueFromLocalizedAttribute(unparsedBirthCity);
            const birthStateProvince =
                unparsedBirthStateProvince !== undefined &&
                parseValueFromLocalizedAttribute(unparsedBirthStateProvince);
            return birthCountry ? (
                <BirthplaceColumn>
                    <FormattedMessage
                        description="Cell that shows the birthplace of a given player"
                        defaultMessage="<city>{birthCity}, </city><stateProvince>{birthStateProvince}, </stateProvince><country>{birthCountry}</country>"
                        values={{
                            birthCity: birthCity,
                            birthCountry: birthCountry,
                            birthStateProvince: birthStateProvince,
                            stateProvince: (text) => birthStateProvince && <span>{text}</span>,
                            city: (text) => birthCity && <span>{text}</span>,
                            country: (text) => birthCountry && <span>{text}</span>,
                        }}
                    />
                </BirthplaceColumn>
            ) : (
                <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
            );
        },
        size: 185,
        sortingFn: ReactTableUtils.sortUndefinedAtBottom(getPlayerBirthplace),
        sortUndefined: false,
        meta: {
            ...leftPaddedColumn(),
            ...addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.birthplace, intl),
        },
    }),
];

const getPlayerBirthplace = (player: Player): string | undefined => {
    const birthCity = parseValueFromLocalizedAttribute(player.birthCity);
    return player.birthCountry ? `${player.birthCountry} ${birthCity}` : undefined;
};

export default columnDefinitions;
